import axios, { AxiosResponse } from 'axios';
import { get } from 'lodash';
import { saveAs } from 'file-saver';
import storage from 'local-storage-fallback';

export function getApiUrl(path: string): string {
  return `${process.env.REACT_APP_API_URL}/api/${path}`;
}

export function getBroadcastingUrl(path: string): string {
  return `${process.env.REACT_APP_API_URL}/api/broadcasting/${path}`;
}

const instance = axios.create({});
instance.defaults.timeout = 1000 * 60 * 15;

export { instance };

export const getToken = () => storage.getItem('token');
export const setToken = (token: string | undefined) =>
  token ? storage.setItem('token', token) : storage.removeItem('token');

export class ResponseError extends Error {
  public response: AxiosResponse;
  public payload: ResponseErrorPayload;

  constructor(response?: AxiosResponse) {
    // No response at all (network error or timeout), or one without a
    // JSON message (e.g. a load balancer's 502 page).
    const message: string =
      response?.data?.message ||
      (response
        ? `The request failed (${response.status}).`
        : "Couldn't reach the server. Check your connection and try again.");
    super(message);
    this.response = response as AxiosResponse;
    this.payload = {
      code: response?.status,
      message,
      errors: response?.data?.errors,
    };
  }
}

export interface ResponseErrorPayload {
  message: string;
  code?: number;
  errors?: object;
}

export const apiRequest = async ({
  url,
  method = 'get',
  formData,
  options,
  data,
  ...payload
}: any) => {
  const token = await storage.getItem('token');
  if (token) {
    instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete instance.defaults.headers.common.Authorization;
  }

  const additionalData: { [key: string]: any } = {};
  if (method === 'get') {
    additionalData.params = data;
  } else {
    additionalData.data = data;
  }

  try {
    const response = await instance({
      url: getApiUrl(url),
      method,
      formData,
      ...additionalData,
      ...options,
      ...payload,
    });
    return response.data;
  } catch (e) {
    throw new ResponseError(e.response);
  }
};

/**
 * POST to an endpoint that returns a file and save it. With a blob response
 * the API's JSON error body arrives as a Blob too, so it is parsed back out
 * to surface the real message.
 */
export async function downloadFile({
  url,
  data,
  filename,
}: {
  url: string;
  data?: object;
  filename: string;
}) {
  try {
    const blob = await apiRequest({
      url,
      method: 'post',
      data,
      responseType: 'blob',
    });
    saveAs(blob, filename);
  } catch (e) {
    const body = e instanceof ResponseError ? e.response?.data : null;
    if (body instanceof Blob) {
      const message = await body
        .text()
        .then(text => JSON.parse(text).message)
        .catch(() => null);
      if (message) throw new Error(message);
    }
    throw e;
  }
}

export function serveFile(filepath: string) {
  return instance({
    url: getApiUrl(filepath),
    method: 'GET',
    responseType: 'blob',
  }).then((response, ...rest) => {
    console.log(response.headers);
    const filename = get(response.headers, 'x-attachment-name', 'ca-file');
    saveAs(new Blob([response.data]), `${filename}`);
    return filename;
  });
}
